import { DomainModel, secureRandomString } from 'domains/models';
import { DisplayType } from '../event_type/displayType';
import { isUrl } from 'infras/validator';
import { LocationType } from '../event_type/locationType';
import { Status } from './status';
import { formatToMills } from 'infras/datetime';
import { HostAssignType } from '../event_type/hostAssignType';
import { SourceType } from '../event_source/sourceType';
import { VisibilityType } from './visibilityType';
import EventMeeting from '../event_meeting';
import { MeetingType } from './meeting_type';
import { Transparency } from './transparency';
import { UserType } from '../event_type_user/user_type';
import { HostOperationType } from '../event_type/hostOperationType';
import { ReservationMethod } from '../event_type/reservationMethod';
import { Priority } from './priority';
import { Label } from './label';
import { Behavior } from './behavior';
import { AttendType } from '../event_type/attendType';

interface Props {
  eventTypeId: number | null;
  projectId: number | null;
  teamId: number;
  uid: string;
  startedAt: string | null;
  endedAt: string | null;
  isAllDay: boolean;
  status: Status;
  name: string;
  coverImage: string | null;
  maxCapacity: number | null;
  description: string | null;
  displayType: DisplayType;
  visibilityType: VisibilityType;
  timeZone: string;
  locationId: LocationType | null;
  access: string | null;
  address: string | null;
  phoneNumber: string | null;
  url: string | null;
  customLocation: string | null;
  hostOperationType: HostOperationType;
  hostAssignType: HostAssignType | null;
  isRemind: boolean;
  slackChannel: string | null;
  sourceType: SourceType;
  htmlLink: string | null;
  cancelReason: string | null;
  cancelledBy: UserType | null;
  cancelledAt: string | null;
  isSeminar: boolean;
  parentEventId: number | null;
  isParent: boolean;
  isBuffer: boolean;
  transparency: Transparency | null;
  reservationMethod: ReservationMethod | null;
  priority: Priority | null;
  label: Label | null;
  behavior: Behavior;
  attendType: AttendType;
}

export default class Event extends DomainModel {
  eventTypeId: number | null;
  projectId: number | null;
  teamId: number;
  uid: string;
  startedAt: string | null;
  endedAt: string | null;
  isAllDay: boolean;
  status: Status;
  name: string;
  coverImage: string | null;
  maxCapacity: number | null;
  description: string | null;
  displayType: DisplayType;
  visibilityType: VisibilityType;
  timeZone: string;
  locationId: LocationType | null;
  access: string | null;
  address: string | null;
  phoneNumber: string | null;
  url: string | null;
  customLocation: string | null;
  hostOperationType: HostOperationType;
  hostAssignType: HostAssignType | null;
  isRemind: boolean;
  slackChannel: string | null;
  sourceType: SourceType;
  htmlLink: string | null;
  cancelReason: string | null;
  cancelledBy: UserType | null;
  cancelledAt: string | null;
  isSeminar: boolean;
  parentEventId: number | null;
  isParent: boolean;
  isBuffer: boolean;
  transparency: Transparency;
  reservationMethod: ReservationMethod | null;
  priority: Priority | null;
  label: Label | null;
  behavior: Behavior;
  attendType: AttendType;

  constructor(props: Props) {
    super(props);
    this._className = 'Event';
    this.eventTypeId = props.eventTypeId;
    this.teamId = props.teamId;
    this.eventTypeId = props.eventTypeId;
    this.uid = props.uid;
    this.status = props.status;
    this.startedAt = props.startedAt;
    this.endedAt = props.endedAt;
    this.isAllDay = props.isAllDay;
    this.name = props.name;
    this.coverImage = props.coverImage || null;
    this.maxCapacity = props.maxCapacity || null;
    this.description = props.description || null;
    this.displayType = props.displayType || null;
    this.visibilityType = props.visibilityType || null;
    this.timeZone = props.timeZone;
    this.access = props.access || null;
    this.address = props.address || null;
    this.locationId = props.locationId || null;
    this.phoneNumber = props.phoneNumber || null;
    this.url = props.url || null;
    this.customLocation = props.customLocation || null;
    this.hostOperationType = props.hostOperationType;
    this.hostAssignType = props.hostAssignType || null;
    this.projectId = props.projectId || null;
    this.isRemind = props.isRemind ? true : false;
    this.slackChannel = props.slackChannel || null;
    this.sourceType = props.sourceType;
    this.htmlLink = props.htmlLink;
    this.cancelReason = props.cancelReason || null;
    this.cancelledBy = props.cancelledBy || null;
    this.cancelledAt = props.cancelledAt || null;
    this.isSeminar = props.isSeminar;
    this.parentEventId = props.parentEventId || null;
    this.isParent = props.isParent;
    this.transparency = props.transparency || Transparency.Opaque;
    this.isBuffer = props.isBuffer;
    this.reservationMethod = props.reservationMethod;
    this.priority = props.priority || null;
    this.label = props.label;
    this.behavior = props.behavior;
    this.attendType = props.attendType || AttendType.Normal;
  }

  static createInternalCalendarEvent(event: {
    teamId: number;
    startedAt: string | null;
    endedAt: string | null;
    isAllDay: boolean;
    name: string;
    locationId: LocationType | null;
    description: string | null;
    displayType: DisplayType;
    visibilityType: VisibilityType;
    timeZone: string;
    access: string | null;
    address: string | null;
    phoneNumber: string | null;
    customLocation: string | null;
    isRemind: boolean;
    transparency: Transparency;
    behavior: Behavior;
    priority: Priority | null;
  }) {
    const newEvent = new Event({
      ...event,
      projectId: null,
      eventTypeId: null,
      uid: secureRandomString(),
      status: Status.Open,
      coverImage: null,
      maxCapacity: null,
      url: null,
      hostAssignType: HostAssignType.None,
      hostOperationType: HostOperationType.None,
      slackChannel: null,
      sourceType: SourceType.InternalCalendar,
      htmlLink: null,
      cancelReason: null,
      cancelledAt: null,
      cancelledBy: null,
      isSeminar: false,
      isParent: false,
      parentEventId: null,
      reservationMethod: null,
      label: null,
      isBuffer: false,
      attendType: AttendType.Normal,
    });
    return newEvent;
  }

  static permited = [
    'startedAt',
    'endedAt',
    'displayType',
    'visibilityType',
    'timeZone',
    'locationId',
    'access',
    'status',
    'address',
    'phoneNumber',
    'url',
    'customLocation',
    'cancelReason',
    'cancelledBy',
    'cancelledAt',
    'name',
    'description',
    'projectId',
    'isAllDay',
    'slackChannel',
    'isRemind',
    'htmlLink',
    'parentEventId',
    'transparency',
    'priority',
    'label',
    'behavior',
    'isSeminar',
    'maxCapacity',
  ];

  static permit(updater: any) {
    return super.permit(updater, this.permited);
  }

  isValid(scopes: string[] | null = null) {
    super.beforeValidate();
    let p = '';

    p = 'eventTypeId';
    if (!scopes || scopes.includes(p)) {
      if (this.eventTypeId) {
        // this.addError(p, 'E001');
      }
    }

    p = 'teamId';
    if (!scopes || scopes.includes(p)) {
      if (!this.teamId) {
        this.addError(p, 'E001');
      }
    }

    p = 'uid';
    if (!scopes || scopes.includes(p)) {
      if (this.uid) {
        if (this.uid.length < 3 || this.uid.length > 64) {
          this.addError(p, 'E002');
        }
      } else {
        this.addError(p, 'E001');
      }
    }

    p = 'name';
    if (!scopes || scopes.includes(p)) {
      if (this.name) {
        if (this.name.length > 64 && this.status !== Status.Reserved) {
          this.addError(p, 'E002');
        }
      } else if (this.name === null) {
        this.addError(p, 'E001');
      }
    }

    p = 'startedAt';
    if (!scopes || scopes.includes(p)) {
      if (this.startedAt) {
      } else {
        // this.addError(p, 'E001');
      }
    }

    p = 'endedAt';
    if (!scopes || scopes.includes(p)) {
      if (this.endedAt) {
      } else {
        // this.addError(p, 'E001');
      }
    }

    p = 'coverImage';
    if (!scopes || scopes.includes(p)) {
      if (this.coverImage) {
      }
    }

    p = 'status';
    if (!scopes || scopes.includes(p)) {
      if (this.status) {
        if (typeof Status[this.status] !== 'string') {
          this.addError(p, 'E002');
        }
      } else {
        this.addError(p, 'E001');
      }
    }

    p = 'displayType';
    if (!scopes || scopes.includes(p)) {
      if (this.displayType) {
        if (typeof DisplayType[this.displayType] !== 'string') {
          this.addError(p, 'E002');
        }
      } else {
        this.addError(p, 'E001');
      }
    }

    p = 'visibilityType';
    if (!scopes || scopes.includes(p)) {
      if (this.visibilityType) {
        if (typeof VisibilityType[this.visibilityType] !== 'string') {
          this.addError(p, 'E002');
        }
      } else {
        this.addError(p, 'E001');
      }
    }

    p = 'timeZone';
    if (!scopes || scopes.includes(p)) {
      if (!this.timeZone) {
        this.addError(p, 'E001');
      }
    }

    p = 'access';
    if (!scopes || scopes.includes(p)) {
      if (this.access && this.access.length > 2000) {
        this.addError(p, 'E002');
      }
    }

    p = 'address';
    if (!scopes || scopes.includes(p)) {
      if (this.address && this.address.length > 255) {
        this.addError(p, 'E002');
      }
    }

    p = 'url';
    if (!scopes || scopes.includes(p)) {
      if (this.url) {
        if (!isUrl(this.url)) {
          this.addError(p, 'E001');
        }
      }
    }

    p = 'phoneNumber';
    if (!scopes || scopes.includes(p)) {
      if (this.phoneNumber) {
      }
    }

    p = 'customLocation';
    if (!scopes || scopes.includes(p)) {
      if (this.customLocation && this.customLocation.length > 2000) {
        this.addError(p, 'E002');
      }
    }

    p = 'cancelReason';
    if (!scopes || scopes.includes(p)) {
      if (this.cancelReason && this.cancelReason.length > 255) {
        this.addError(p, 'E001');
      }
    }

    p = 'behavior';
    if (!scopes || scopes.includes(p)) {
      if (![Behavior.Event, Behavior.Work].includes(this.behavior)) {
        this.addError(p, 'E001');
      }
    }
    p = 'description';
    if (!scopes || scopes.includes(p)) {
      if (this.description && this.description.length > 2000) {
        this.addError(p, 'E002');
      }
    }
    p = 'isSeminar';
    if (!scopes || scopes.includes(p)) {
      if (this.isSeminar) {
      }
    }
    p = 'maxCapacity';
    if (!scopes || scopes.includes(p)) {
      if (this.maxCapacity) {
      }
    }
    return super.isValid();
  }

  meetings(eventMeetings: EventMeeting[]) {
    const mergedEventMeetings: EventMeeting[] = [...eventMeetings];
    if (this.url) {
      if (
        eventMeetings.every((m) => m.meetingType !== MeetingType.GoogleMeet) &&
        RegExp(/^https:\/\/meet\.google\.com/).test(this.url)
      ) {
        mergedEventMeetings.push(
          new EventMeeting({
            eventId: this.id!,
            meetingType: MeetingType.GoogleMeet,
            isPrimary: false,
            url: this.url,
            meetingId: null,
          }),
        );
      } else if (
        eventMeetings.every((m) => m.meetingType !== MeetingType.Zoom) &&
        RegExp(/^https:\/\/.*zoom.us\//).test(this.url)
      ) {
        mergedEventMeetings.push(
          new EventMeeting({
            eventId: this.id!,
            meetingType: MeetingType.Zoom,
            isPrimary: true,
            url: this.url,
            meetingId: null,
          }),
        );
      }
    }
    return mergedEventMeetings;
  }

  static duration(startedAt: string, endedAt: string) {
    const startTime = formatToMills(startedAt);
    const endTime = formatToMills(endedAt);
    return (endTime - startTime) / 1000 / 60;
  }

  static getFromEmail(eventUid: string, eventUserId: number) {
    return `event.${eventUid}.${eventUserId}@${String(
      process.env.SENDGRID_REPLY_DOMAIN,
    )}`;
  }

  static getCheckoutSessionAge() {
    // 5分で有効期限切れにする
    return 1000 * 60 * 5;
  }

  static getHostTimeZone(
    eventTimeZone: string,
    eventTypeTimeZone?: string,
  ): string {
    if (eventTypeTimeZone) {
      return eventTypeTimeZone;
    }
    return eventTimeZone;
  }
}
